const wait = (duration: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("The request was cancelled.", "AbortError"));
      return;
    }

    const handleAbort = () => {
      globalThis.clearTimeout(timeoutId);
      reject(new DOMException("The request was cancelled.", "AbortError"));
    };
    const timeoutId = globalThis.setTimeout(() => {
      signal.removeEventListener("abort", handleAbort);
      resolve();
    }, duration);
    signal.addEventListener("abort", handleAbort, { once: true });
  });

export type TJobMetadataPollingDiagnostics = {
  poll_attempts: number;
  last_http_status: number | null;
  http_403_count: number;
  http_404_count: number;
  network_error_count: number;
  last_network_error_name: string | null;
  last_network_error_message: string | null;
  polling_elapsed_ms: number;
};

const pollingDiagnosticsByError = new WeakMap<
  object,
  TJobMetadataPollingDiagnostics
>();

export const getJobMetadataPollingDiagnostics = (
  error: unknown,
): TJobMetadataPollingDiagnostics | null =>
  error && typeof error === "object"
    ? (pollingDiagnosticsByError.get(error) ?? null)
    : null;

const attachPollingDiagnostics = <T extends Error>(
  error: T,
  diagnostics: TJobMetadataPollingDiagnostics,
): T => {
  pollingDiagnosticsByError.set(error, diagnostics);
  return error;
};

const getErrorName = (error: unknown): string =>
  error instanceof Error ? error.name : typeof error;

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

export const waitForJobMetadata = async <T>({
  metadataUrl,
  signal,
  intervalMs,
  timeoutMs,
  statusLabel,
  fetchMetadata = globalThis.fetch,
  resolveResponse = (response) => response.json() as Promise<T>,
}: {
  metadataUrl: string;
  signal?: AbortSignal;
  intervalMs: number;
  timeoutMs: number;
  statusLabel: string;
  fetchMetadata?: typeof fetch;
  resolveResponse?: (response: Response) => Promise<T>;
}): Promise<T> => {
  const pollingController = new AbortController();
  const startedAt = Date.now();
  let timedOut = false;
  let pollAttempts = 0;
  let lastHttpStatus: number | null = null;
  let http403Count = 0;
  let http404Count = 0;
  let networkErrorCount = 0;
  let lastNetworkErrorName: string | null = null;
  let lastNetworkErrorMessage: string | null = null;
  const handleCallerAbort = () => pollingController.abort();

  const getDiagnostics = (): TJobMetadataPollingDiagnostics => ({
    poll_attempts: pollAttempts,
    last_http_status: lastHttpStatus,
    http_403_count: http403Count,
    http_404_count: http404Count,
    network_error_count: networkErrorCount,
    last_network_error_name: lastNetworkErrorName,
    last_network_error_message: lastNetworkErrorMessage,
    polling_elapsed_ms: Math.max(0, Date.now() - startedAt),
  });

  if (signal?.aborted) {
    handleCallerAbort();
  } else {
    signal?.addEventListener("abort", handleCallerAbort, { once: true });
  }

  const timeoutId = globalThis.setTimeout(() => {
    timedOut = true;
    pollingController.abort();
  }, timeoutMs);

  // Abort must settle our wait even if fetch or a response-body read stalls.
  let rejectOnAbort: () => void;
  const aborted = new Promise<never>((_, reject) => {
    rejectOnAbort = () =>
      reject(new DOMException("The request was cancelled.", "AbortError"));
    pollingController.signal.addEventListener("abort", rejectOnAbort, {
      once: true,
    });
  });

  try {
    while (true) {
      if (pollingController.signal.aborted) {
        throw new DOMException("The request was cancelled.", "AbortError");
      }

      pollAttempts += 1;
      const response = await Promise.race([
        fetchMetadata(metadataUrl, {
          method: "GET",
          cache: "no-store",
          signal: pollingController.signal,
        }).catch((error) => {
          if (pollingController.signal.aborted) throw error;
          networkErrorCount += 1;
          lastNetworkErrorName = getErrorName(error);
          lastNetworkErrorMessage = getErrorMessage(error);
          return null;
        }),
        aborted,
      ]);

      if (response) {
        lastHttpStatus = response.status;
        if (response.status === 403) http403Count += 1;
        if (response.status === 404) http404Count += 1;
      }

      if (response?.ok) {
        // Await the body before finally removes the timeout/abort handlers.
        return await Promise.race([resolveResponse(response), aborted]);
      }

      if (response && response.status !== 403 && response.status !== 404) {
        throw new Error(
          `${statusLabel} status could not be checked (HTTP ${response.status}).`,
        );
      }

      await wait(intervalMs, pollingController.signal);
    }
  } catch (error) {
    if (timedOut) {
      throw attachPollingDiagnostics(
        new Error(
          `${statusLabel} metadata was unavailable after ${pollAttempts} polling attempt${pollAttempts === 1 ? "" : "s"}.`,
        ),
        getDiagnostics(),
      );
    }
    if (signal?.aborted) {
      throw attachPollingDiagnostics(
        new DOMException("The request was cancelled.", "AbortError"),
        getDiagnostics(),
      );
    }
    throw attachPollingDiagnostics(
      error instanceof Error ? error : new Error(getErrorMessage(error)),
      getDiagnostics(),
    );
  } finally {
    globalThis.clearTimeout(timeoutId);
    pollingController.signal.removeEventListener("abort", rejectOnAbort!);
    signal?.removeEventListener("abort", handleCallerAbort);
  }
};
